<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import type { Device } from 'src/types/Device';
	import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
	import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
	import { user_data } from 'src/stores';
	import VideoContainer from './VideoContainer.svelte';
	import WebRTCPlayer from './WebRTCPlayer.svelte';
	import RecordingIndicator from './RecordingIndicator.svelte';
	import CameraActions from '../DeviceDrawer/CameraActions.svelte';
	import { subscribeRecordingDuration } from '../helpers/recording-timer';
	import { computeCameraState, isCameraFirmwareUpdateRequired } from '../helpers/camera-state-helpers';
	import { hasActiveWebRTCSession } from '../helpers/webrtc-session-manager';
	import {
		getCameraComponentKey,
		getManualRecording,
		isAnyStorageAvailable,
		isCameraPrivacyEnabled,
	} from '../helpers/camera-helpers';
	import type { CameraRecordings, CameraStreamer } from '../types/camera-status';
	import { PttUiState } from '../types/ptt-ui-state';
	import type { WebRTCPlayerSession } from '../types/webrtc-session';

	export let device: Device;
	export let status: DeviceStatusG2;
	export let settings: DeviceSettingsG2;
	export let showInfo = false;

	let recordingDuration = '';
	let durationCleanup: (() => void) | null = null;
	let player: WebRTCPlayer;
	let session: WebRTCPlayerSession = null;
	let isZoomed = false;
	let playerRoot: HTMLElement;

	$: cameraKey = getCameraComponentKey(device);
	$: recordings = status?.[cameraKey]?.recordings as CameraRecordings | undefined;
	$: hasStorage = isAnyStorageAvailable(device, status);
	$: activeRecording = getManualRecording(recordings);
	$: recordingStartedAt = activeRecording && hasStorage ? activeRecording.ts * 1000 : null;

	$: {
		if (durationCleanup) durationCleanup();
		durationCleanup = subscribeRecordingDuration(recordingStartedAt, (d) => (recordingDuration = d));
	}

	$: isOnline = status?.online ?? false;
	$: isPrivacyMode = isCameraPrivacyEnabled(device, status, settings);
	$: isSupportAccess = $user_data?.temporary === true;
	$: streamer = (status?.[cameraKey] as { streamer?: CameraStreamer })?.streamer;
	$: firmwareUpdateRequired = isCameraFirmwareUpdateRequired(device, status);
	$: streams = (status?.[cameraKey] as { streams?: number })?.streams;
	// Unified camera-level block: support access / offline / firmware / privacy /
	// camera full / streamer not running. When non-null, WebRTCPlayer must not be
	// mounted.
	// `hasActiveWebRTCSession` reads the session map and is not reactive, so it
	// is called here rather than in its own `$:` — the `streams` push is what
	// re-evaluates this, and that is the only value the session gates.
	$: cameraState = computeCameraState(
		isPrivacyMode,
		isOnline,
		streamer,
		firmwareUpdateRequired,
		streams,
		hasActiveWebRTCSession(device?.id),
		isSupportAccess,
	);
	$: canPlayStream = !cameraState;

	// The player reports its own connection and playback state; the camera-level
	// block always wins over it.
	$: videoState = cameraState ?? $session?.streamState ?? null;
	$: streamLoading = $session?.streamState?.type === 'loading';
	$: pttState = $session?.pttState ?? PttUiState.UNAVAILABLE;

	// The shell keeps this mounted and only slides it off-screen, so closing the
	// drawer never triggers `onDestroy`. Rather than infer the drawer state from
	// a store (which lags the real transition) watch the open class.
	onMount(() => {
		const panel = playerRoot?.closest('.drawer, .sidepanel');
		if (!panel) return;

		let wasOpen = panel.classList.contains('open');
		// The player joins its session on mount, so a panel that starts closed
		// has to hand the camera's stream slot straight back.
		if (!wasOpen) player?.stop();

		const observer = new MutationObserver(() => {
			const isOpen = panel.classList.contains('open');
			// Closing only leaves the camera's shared session; reopening has to
			// re-join it, since the player was never destroyed.
			if (wasOpen && !isOpen) player?.stop();
			if (!wasOpen && isOpen) player?.start();
			wasOpen = isOpen;
		});
		observer.observe(panel, { attributes: true, attributeFilter: ['class'] });

		return () => observer.disconnect();
	});

	onDestroy(() => {
		if (durationCleanup) durationCleanup();
	});
</script>

<div class="player-root" bind:this={playerRoot}>
	<VideoContainer state={videoState} bind:isZoomed on:retry={() => session?.retry()}>
		{#if canPlayStream}
			<WebRTCPlayer
				bind:this={player}
				bind:session
				deviceId={device.id}
				showControls={!isZoomed}
				{showInfo}
			/>
		{/if}
		<RecordingIndicator
			slot="topLeft"
			visible={!!recordingStartedAt && canPlayStream}
			duration={recordingDuration}
		/>
		<CameraActions
			slot="actions"
			{device}
			bind:status
			bind:settings
			{pttState}
			{streamLoading}
			on:talkToggle={(e) => session?.requestTalk(e.detail)}
		/>
	</VideoContainer>
</div>

<style>
	.player-root {
		display: contents;
	}
</style>
